import * as Y from 'yjs';
import * as sync from 'y-protocols/sync';
import * as awareness from 'y-protocols/awareness';
import * as encoding from 'lib0/encoding';
import * as decoding from 'lib0/decoding';
import { io } from 'socket.io-client';
import config from '../../constants/config';


function logYjsState(doc, label = 'Doc State') {
  console.group(`🔍 ${label}`);

  // 1. Client ID
  console.log('clientID:', doc.clientID);

  // 2. Which clients exist in the store
  const clients = Array.from(doc.store.clients.keys());
  console.log('Clients in store:', clients);

  // 3. Shared type names
  const sharedTypes = Array.from(doc.share.keys());
  console.log('Shared types:', sharedTypes);

  console.groupEnd();
}

// Helper to safely convert binary data
const toUint8Array = (data) => {
  if (data == null) return new Uint8Array(0);
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (data.buffer instanceof ArrayBuffer) return new Uint8Array(data.buffer);
  if (Array.isArray(data)) return new Uint8Array(data);
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
    return new Uint8Array(data);
  }
  console.error('[toUint8Array] Unsupported type:', typeof data, data);
  return new Uint8Array(0);
};

/**
 * Factory: creates AND connects a namespaced Socket.IO Yjs provider.
 * @param {string} whiteboardId
 * @param {Object} userInfo
 * @returns {Object}
 */
export function createNamespacedProvider(whiteboardId, userInfo) {
  if (!whiteboardId) {
    console.error('[NamespacedProvider] No whiteboardId provided');
    return null;
  }

  // 1. Create pristine doc — NO awareness yet
  const doc = new Y.Doc();

  let awarenessInstance = null;
  let awarenessChangeHandler = null;
  let updateHandlerAttached = false;
  let synced = false;
  const awarenessReadyCallbacks = [];

  const baseURL = config.SERVER_URL || 'http://localhost:3001';
  const nsp = `/${whiteboardId}`;

  const socket = io(baseURL + nsp, {
    transports: ['websocket'],
    withCredentials: true,
    autoConnect: false,
    forceNew: true,
    multiplex: false,
    auth: {
      token: userInfo?.token 
    },
    query: {
      email: userInfo?.email,
      id: userInfo?.id
    }
  });

  // Color helper
  const hsl = (seed = '') => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h);
    return `hsl(${Math.abs(h % 360)}, 70%, 50%)`;
  };

  // Encapsulated awareness setup - ONLY called after sync-step-2
  const initializeAwareness = () => {
    if (awarenessInstance) {
      console.warn('[NamespacedProvider] Awareness already initialized');
      return;
    }

    console.log('[NamespacedProvider] Initializing awareness after sync');

    // Create awareness instance
    awarenessInstance = new awareness.Awareness(doc);

    // Set local user state
    awarenessInstance.setLocalStateField('user', {
      name: userInfo?.name || 'Anonymous',
      email: userInfo?.email || '',
      id: userInfo?.id || '',
      color: userInfo?.color || hsl(userInfo?.email)
    });

    // Set up awareness change handler
    awarenessChangeHandler = ({ added, updated, removed }, origin) => {
      if (origin === socket) return;
      const changedClients = added.concat(updated).concat(removed);
      const update = awareness.encodeAwarenessUpdate(awarenessInstance, changedClients);
      socket.emit('awareness-update', update.buffer);
    };

    awarenessInstance.on('change', awarenessChangeHandler);

    // Broadcast initial awareness state AFTER setup is complete
    const awarenessUpdate = awareness.encodeAwarenessUpdate(
      awarenessInstance,
      [doc.clientID]
    );
    socket.emit('awareness-update', awarenessUpdate.buffer);

    console.log('[NamespacedProvider] Awareness initialized and broadcasted');

    // ✅ Notify all waiting callbacks that awareness is ready
    awarenessReadyCallbacks.forEach(callback => {
      try {
        callback(awarenessInstance);
      } catch (e) {
        console.error('[NamespacedProvider] Error in awareness ready callback:', e);
      }
    });
    awarenessReadyCallbacks.length = 0; // Clear callbacks
  };

  // Cleanup awareness
  const destroyAwareness = () => {
    if (awarenessInstance && awarenessChangeHandler) {
      awarenessInstance.off('change', awarenessChangeHandler);
      awarenessChangeHandler = null;
    }
    if (awarenessInstance) {
      awarenessInstance.destroy();
      awarenessInstance = null;
    }
  };

  /* ===== Update handler (for doc changes) ===== */
  const updateHandler = (update, origin) => {
    if (origin !== socket && synced) {
      socket.emit('update', update.buffer);
    }
  };

  /* ===== Socket Events ===== */

  socket.on('connect', () => {
    console.log(`[NamespacedProvider] Connected to ${nsp}`);
    synced = false;
    // Do NOT send awareness here — wait for sync
  });

  socket.on('sync-step-2', (buf) => {
    console.log('[NamespacedProvider] Received sync-step-2 from server');
    try {
      const decoder = decoding.createDecoder(toUint8Array(buf));
      const encoder = encoding.createEncoder();
      logYjsState(doc, `Before Sync-Step-2 from ${socket.id}`);
      sync.readSyncMessage(decoder, encoder, doc, socket, (error) => {
        if (error) {
          console.error('[NamespacedProvider] Error applying sync-step-2:', error);
        }
      });

      synced = true;
      console.log('[NamespacedProvider] Initial sync complete');

      // Attach update handler FIRST
      if (!updateHandlerAttached) {
        doc.on('update', updateHandler);
        updateHandlerAttached = true;
        console.log('[NamespacedProvider] Update handler attached');
      }

      // ✅ THEN initialize awareness (only after sync is complete)
      initializeAwareness();

    } catch (e) {
      console.error('[NamespacedProvider] Error handling sync-step-2:', e);
    }
  });

  socket.on('update', (buf) => {
    try {
      const update = toUint8Array(buf);
      Y.applyUpdate(doc, update, socket);
    } catch (e) {
      console.error('[NamespacedProvider] Error handling update:', e);
    }
  });

  socket.on('awareness', (buf) => {
    try {
      if (!buf) return;
      const update = toUint8Array(buf);
      if (update.length === 0) return;
      // Only apply if awareness is initialized
      if (awarenessInstance) {
        awareness.applyAwarenessUpdate(awarenessInstance, update, socket);
      }
    } catch (e) {
      console.error('[NamespacedProvider] Error handling awareness:', e);
    }
  });

  socket.on('disconnect', () => {
    console.log('[NamespacedProvider] Disconnected from server');
    synced = false;
    // Clean up awareness on disconnect
    destroyAwareness();
  });

  /* ===== Cleanup ===== */
  const destroy = () => {
    console.log('[NamespacedProvider] Destroying provider');

    // Remove update handler
    if (updateHandlerAttached) {
      doc.off('update', updateHandler);
      updateHandlerAttached = false;
    }

    // Destroy awareness
    destroyAwareness();

    // Disconnect socket
    socket.disconnect();

    // Destroy document
    doc.destroy();

    console.log('[NamespacedProvider] Provider destroyed');
  };

  const provider = {
    doc,
    get awareness() {
      return awarenessInstance;
    },
    // Method to subscribe to awareness initialization
    onAwarenessReady: (callback) => {
      if (awarenessInstance) {
        // Already initialized, call immediately
        callback(awarenessInstance);
      } else {
        // Wait for initialization
        awarenessReadyCallbacks.push(callback);
      }
    },
    getParticipantCount: () => {
      if (!awarenessInstance) return 1;
      // Count only states with actual user data
      let count = 0;
      awarenessInstance.getStates().forEach((state) => {
        if (state && state.user) {
          count++;
        }
      });
      return count;
    },
    socket,
    synced: () => synced,
    connect: () => socket.connect(),
    disconnect: () => socket.disconnect(),
    destroy
  };

  console.log(`[NamespacedProvider] Created for namespace ${nsp}`);
  return provider;
}